import Button from "@/components/Button";
import Select from "@/components/Select";
import { ApolloError, useMutation, useQuery } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";
import {
  CREATE_PERSON_SECTION_CHAPTER_AFFILIATION_MUTATION,
  DELETE_PERSON_SECTION_CHAPTER_AFFILIATION_MUTATION,
  SECTION_CHAPTER_OPTIONS_BY_SECTION_IDS_QUERY,
  UPDATE_PERSON_SECTION_CHAPTER_AFFILIATION_MUTATION
} from "@/features/person/queries";
import { Person, SectionAffiliation } from "@/types";

type ChapterOption = {
  id: string;
  code: string;
  name: string;
};

type SectionChapterOptionsByIdsResponse = {
  sections: Array<{
    id: string;
    codedChapters: ChapterOption[];
  }>;
};

interface SectionChapterAffiliationsEditFormProps {
  person: Person;
  onCancel(): void;
  onSuccess(updatedPerson: Partial<Person>): void;
  onError(error: string): void;
}

const defaultErrorMessage = "Failed to update chapter affiliations";
type MutationError = ApolloError | Error | undefined;

const getActiveSectionAffiliations = (person: Person): SectionAffiliation[] => {
  const active = person.activeSectionAffiliations || [];
  if (active.length === 0) return [];

  const primaryAffiliation = active.find((affiliation) => affiliation.primary) || active[0];
  const secondaryAffiliations = active.filter((affiliation) => affiliation.id !== primaryAffiliation?.id);

  return primaryAffiliation ? [primaryAffiliation, ...secondaryAffiliations] : secondaryAffiliations;
};

const errorMessageFrom = (error: MutationError, fallback = defaultErrorMessage): string => {
  if (error instanceof ApolloError) {
    const graphQLErrorMessage = error.graphQLErrors[0]?.message;
    const networkErrorMessage = error.networkError?.message;
    return graphQLErrorMessage || networkErrorMessage || error.message || fallback;
  }
  if (error) return error.message || fallback;
  return fallback;
};

export default function SectionChapterAffiliationsEditForm({
  person,
  onCancel,
  onSuccess,
  onError
}: SectionChapterAffiliationsEditFormProps) {
  const activeAffiliations = useMemo(() => getActiveSectionAffiliations(person), [person]);
  const [selectedByChapterAffiliationId, setSelectedByChapterAffiliationId] = useState<Record<string, string>>({});
  const [newChapterByAffiliationId, setNewChapterByAffiliationId] = useState<Record<string, string>>({});

  const [createChapterAffiliation, { loading: creating }] = useMutation(
    CREATE_PERSON_SECTION_CHAPTER_AFFILIATION_MUTATION
  );
  const [updateChapterAffiliation, { loading: updating }] = useMutation(
    UPDATE_PERSON_SECTION_CHAPTER_AFFILIATION_MUTATION
  );
  const [deleteChapterAffiliation, { loading: deleting }] = useMutation(
    DELETE_PERSON_SECTION_CHAPTER_AFFILIATION_MUTATION
  );

  const loading = creating || updating || deleting;
  const sectionIds = useMemo(
    () => Array.from(new Set(activeAffiliations.map((affiliation) => affiliation.section?.id).filter(Boolean))),
    [activeAffiliations]
  );

  const { data: sectionOptionsData, error: sectionOptionsError } = useQuery<SectionChapterOptionsByIdsResponse>(
    SECTION_CHAPTER_OPTIONS_BY_SECTION_IDS_QUERY,
    {
      variables: { sectionIds },
      skip: sectionIds.length === 0,
      fetchPolicy: "network-only"
    }
  );

  const chapterOptionsByAffiliationId = useMemo(() => {
    const chaptersBySectionId = new Map(
      (sectionOptionsData?.sections || []).map((section) => [section.id, section.codedChapters])
    );

    return Object.fromEntries(
      activeAffiliations.map((affiliation) => [
        affiliation.id,
        chaptersBySectionId.get(affiliation.section?.id || "") || []
      ])
    );
  }, [activeAffiliations, sectionOptionsData]);

  useEffect(() => {
    setSelectedByChapterAffiliationId(() => {
      const next: Record<string, string> = {};
      activeAffiliations.forEach((affiliation) => {
        (affiliation.chapterAffiliations || []).forEach((chapterAffiliation) => {
          next[chapterAffiliation.id] = chapterAffiliation.chapter.id;
        });
      });
      return next;
    });
  }, [activeAffiliations]);

  useEffect(() => {
    if (sectionOptionsError) {
      onError(errorMessageFrom(sectionOptionsError as MutationError, "Failed to load section chapter options."));
    }
  }, [onError, sectionOptionsError]);

  const applyMutationResult = (result: any, responseKey: string) => {
    const payload = result?.data?.[responseKey];
    if (!payload?.success || !payload.person) {
      onError(payload?.message || defaultErrorMessage);
      return;
    }

    onSuccess({
      primarySectionAffiliation: payload.person.primarySectionAffiliation,
      sectionAffiliations: payload.person.sectionAffiliations,
      activeSectionAffiliations: payload.person.sectionAffiliations
    });
  };

  const handleUpdate = async (chapterAffiliationId: string, chapterId: string) => {
    try {
      const result = await updateChapterAffiliation({
        variables: { input: { id: chapterAffiliationId, chapterId } }
      });
      applyMutationResult(result, "updatePersonSectionChapterAffiliation");
    } catch (error) {
      onError(errorMessageFrom(error as MutationError));
    }
  };

  const handleDelete = async (chapterAffiliationId: string) => {
    try {
      const result = await deleteChapterAffiliation({
        variables: { input: { id: chapterAffiliationId } }
      });
      applyMutationResult(result, "deletePersonSectionChapterAffiliation");
    } catch (error) {
      onError(errorMessageFrom(error as MutationError));
    }
  };

  const handleCreate = async (sectionAffiliationId: string) => {
    const chapterId = newChapterByAffiliationId[sectionAffiliationId];
    if (!chapterId) return;

    try {
      const result = await createChapterAffiliation({
        variables: {
          input: {
            personId: person.id,
            sectionAffiliationId,
            chapterId
          }
        }
      });
      applyMutationResult(result, "createPersonSectionChapterAffiliation");
    } catch (error) {
      onError(errorMessageFrom(error as MutationError));
    }
  };

  return (
    <div className="flex flex-col gap-6" data-testid="section-chapter-affiliations-edit-form">
      {activeAffiliations.length === 0 ? (
        <p className="text-body-medium text-sys-brand-on-surface-variant">No active section affiliations</p>
      ) : (
        activeAffiliations.map((affiliation) => {
          const options = (chapterOptionsByAffiliationId[affiliation.id] || []).map((chapter) => ({
            value: chapter.id,
            label: chapter.name
          }));
          const usedChapterIds = new Set(
            (affiliation.chapterAffiliations || []).map((chapterAffiliation) => chapterAffiliation.chapter.id)
          );
          const addOptions = options.filter((option) => !usedChapterIds.has(option.value));
          const hasAddOptions = addOptions.length > 0;

          return (
            <div key={affiliation.id} className="flex flex-col gap-4 border border-sys-brand-outline rounded-lg p-4">
              <p className="text-title-medium-bold">
                {affiliation.primary ? "Primary Section" : "Secondary Section"}: {affiliation.section?.name}
              </p>

              {(affiliation.chapterAffiliations || []).map((chapterAffiliation) => {
                const selectedChapterId =
                  selectedByChapterAffiliationId[chapterAffiliation.id] || chapterAffiliation.chapter.id;
                const isChanged = selectedChapterId !== chapterAffiliation.chapter.id;

                return (
                  <div key={chapterAffiliation.id} className="flex flex-col gap-3 md:flex-row md:items-end">
                    <div className="flex-1">
                      <Select
                        label="Section Chapter"
                        options={options}
                        value={selectedChapterId}
                        onSelect={(value) =>
                          setSelectedByChapterAffiliationId((prev) => ({
                            ...prev,
                            [chapterAffiliation.id]: value
                          }))
                        }
                        disabled={loading || options.length === 0}
                        data-testid={`chapter-affiliation-select-${chapterAffiliation.id}`}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        disabled={loading || !isChanged || !selectedChapterId}
                        onClick={() => handleUpdate(chapterAffiliation.id, selectedChapterId)}
                      >
                        Save
                      </Button>
                      <Button variant="text" disabled={loading} onClick={() => handleDelete(chapterAffiliation.id)}>
                        Delete
                      </Button>
                    </div>
                  </div>
                );
              })}

              <div className="flex flex-col gap-3 md:flex-row md:items-end">
                <div className="flex-1">
                  <Select
                    label="Add Section Chapter"
                    options={addOptions}
                    value={newChapterByAffiliationId[affiliation.id] || ""}
                    onSelect={(value) =>
                      setNewChapterByAffiliationId((prev) => ({
                        ...prev,
                        [affiliation.id]: value
                      }))
                    }
                    disabled={loading || !hasAddOptions}
                    placeholder={hasAddOptions ? "Select a chapter" : "No chapters available"}
                    data-testid={`chapter-affiliation-add-select-${affiliation.id}`}
                  />
                </div>
                <Button
                  variant="filledTonal"
                  disabled={loading || !newChapterByAffiliationId[affiliation.id]}
                  onClick={() => handleCreate(affiliation.id)}
                >
                  Add Chapter Affiliation
                </Button>
              </div>
            </div>
          );
        })
      )}

      <div className="flex flex-wrap justify-end gap-1 items-center">
        <Button variant="text" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
